'use client';

import { createContext, useState, useContext, useCallback } from 'react';
import Toast from './Toast';

const ToastContext = createContext({
  addToast: () => {},
  removeToast: () => {}
});

/**
 * Returns {addToast, removeToast}: Ways to add and remove toasts.
 *
 * You can add a toast with:
 * addToast(content, { type = "neutral", timer = 4000 })
 * where content is any React component,
 * type is "neutral" | "error" | "positive" | "warning",
 * and timer is the ms before the toast is removed (use null for no timer at all).
 */
export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    // Mark toast as closed so that the closing animation can play
    setToasts((prevToasts) =>
      prevToasts.map((toast) =>
        toast.id === id ? { ...toast, closed: true } : toast
      )
    );

    // And then actually remove the toast from the list after a delay
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 1000);
  }, []);

  const addToast = useCallback(
    (content, { type = 'neutral', timer } = {}) => {
      const defaultTimer = type === 'error' ? null : 4000;
      const toastTimer = timer !== undefined ? timer : defaultTimer;

      // Generate unique ID using time. Why not toasts.length? This way avoids
      // adding dependencies to useCallback (+ prevents exhaustive deps warning).
      const id = Date.now() + Math.random();

      setToasts((prevToasts) => [
        ...prevToasts,
        { id, content, type, timer: toastTimer }
      ]);

      if (toastTimer) {
        setTimeout(() => removeToast(id), toastTimer);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-1000000 flex h-full w-full flex-col-reverse items-center justify-start px-4 py-6 sm:flex-col sm:items-end sm:p-6"
      >
        {toasts.map((toast) => (
          <Toast {...toast} key={toast.id} removeToast={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
